/**
 * Terminal 82 - Rarity Statistics
 *
 * Calculates rarity tier distribution from historical data using percentage-based classification.
 */

import type { DayData, RarityTier } from '../types';
import {
  MYTHIC_EVENT_THRESHOLDS,
  PERCENT_THRESHOLDS,
  TIER_COLORS,
  TIER_LABELS,
  MYTHIC_EVENT_COLORS,
  SPECIAL_DAYS,
  getSpecialDayConfig,
} from '../types';

// ============================================
// Types
// ============================================

/**
 * Hidden trait labels for cards
 * These appear in the Market section as cryptic data
 */
export type MomentumTrait = 'FLATLINE' | 'DRIFT' | 'MOVE' | 'SURGE' | 'EXPLOSION' | 'PARABOLIC';
export type VolatilityTrait = 'CALM' | 'STEADY' | 'ACTIVE' | 'VOLATILE' | 'WILD' | 'CHAOS';
export type VolumeTrait = 'SILENT' | 'QUIET' | 'NORMAL' | 'ELEVATED' | 'HEAVY' | 'MASSIVE' | 'TSUNAMI';

export interface CardTraits {
  momentum: MomentumTrait;
  volatility: VolatilityTrait;
  volume: VolumeTrait;
}

export interface RarityStats {
  total: number;
  rarityTiers: Record<RarityTier, RarityTierBucket>;
}

export interface RarityTierBucket {
  count: number;
  percent: number;
  label: string;
  color: string;
  threshold: string;
}

// ============================================
// Rarity Calculation
// ============================================

/**
 * Classify a percentage change into a rarity tier
 * Uses PERCENT_THRESHOLDS from types module
 */
function classifyRarity(changePercent: number): RarityTier {
  const absChange = Math.abs(changePercent);
  if (absChange >= PERCENT_THRESHOLDS.mythic) return 'mythic';
  if (absChange >= PERCENT_THRESHOLDS.legendary) return 'legendary';
  if (absChange >= PERCENT_THRESHOLDS.epic) return 'epic';
  if (absChange >= PERCENT_THRESHOLDS.rare) return 'rare';
  if (absChange >= PERCENT_THRESHOLDS.uncommon) return 'uncommon';
  return 'common';
}

/**
 * Calculate rarity statistics from historical data
 * Uses percentage-based classification for rarity tiers
 */
export function calculateRarityStats(data: DayData[]): RarityStats {
  if (data.length === 0) {
    return getEmptyStats();
  }

  const rarityTiers: Record<RarityTier, RarityTierBucket> = {
    common: { count: 0, percent: 0, label: TIER_LABELS.common, color: TIER_COLORS.common, threshold: '<4%' },
    uncommon: { count: 0, percent: 0, label: TIER_LABELS.uncommon, color: TIER_COLORS.uncommon, threshold: '4-7%' },
    rare: { count: 0, percent: 0, label: TIER_LABELS.rare, color: TIER_COLORS.rare, threshold: '7-12%' },
    epic: { count: 0, percent: 0, label: TIER_LABELS.epic, color: TIER_COLORS.epic, threshold: '12-20%' },
    legendary: { count: 0, percent: 0, label: TIER_LABELS.legendary, color: TIER_COLORS.legendary, threshold: '20-30%' },
    mythic: { count: 0, percent: 0, label: TIER_LABELS.mythic, color: TIER_COLORS.mythic, threshold: '>30%' },
  };

  for (const day of data) {
    const tier = classifyRarity(day.change.percent);
    rarityTiers[tier].count++;
  }

  // Calculate percentages
  const total = data.length;
  for (const key of Object.keys(rarityTiers) as RarityTier[]) {
    rarityTiers[key].percent = (rarityTiers[key].count / total) * 100;
  }

  return { total, rarityTiers };
}

/**
 * Get empty stats for initial state
 */
function getEmptyStats(): RarityStats {
  return {
    total: 0,
    rarityTiers: {
      common: { count: 0, percent: 0, label: TIER_LABELS.common, color: TIER_COLORS.common, threshold: '<4%' },
      uncommon: { count: 0, percent: 0, label: TIER_LABELS.uncommon, color: TIER_COLORS.uncommon, threshold: '4-7%' },
      rare: { count: 0, percent: 0, label: TIER_LABELS.rare, color: TIER_COLORS.rare, threshold: '7-12%' },
      epic: { count: 0, percent: 0, label: TIER_LABELS.epic, color: TIER_COLORS.epic, threshold: '12-20%' },
      legendary: { count: 0, percent: 0, label: TIER_LABELS.legendary, color: TIER_COLORS.legendary, threshold: '20-30%' },
      mythic: { count: 0, percent: 0, label: TIER_LABELS.mythic, color: TIER_COLORS.mythic, threshold: '>30%' },
    },
  };
}

/**
 * Format rarity percentage for display
 */
export function formatRarityPercent(percent: number): string {
  if (percent < 0.1) {
    return '<0.1%';
  }
  if (percent < 1) {
    return percent.toFixed(1) + '%';
  }
  return percent.toFixed(0) + '%';
}

/**
 * Get rarity tier info for a given price change
 */
export function getRarityInfo(changePercent: number): {
  tier: RarityTier;
  label: string;
  color: string;
  frequency: string;
} {
  const tier = classifyRarity(changePercent);
  const info: Record<RarityTier, { label: string; color: string; frequency: string }> = {
    common: { label: TIER_LABELS.common, color: TIER_COLORS.common, frequency: '~70%' },
    uncommon: { label: TIER_LABELS.uncommon, color: TIER_COLORS.uncommon, frequency: '~17%' },
    rare: { label: TIER_LABELS.rare, color: TIER_COLORS.rare, frequency: '~9%' },
    epic: { label: TIER_LABELS.epic, color: TIER_COLORS.epic, frequency: '~4%' },
    legendary: { label: TIER_LABELS.legendary, color: TIER_COLORS.legendary, frequency: '~0.6%' },
    mythic: { label: TIER_LABELS.mythic, color: TIER_COLORS.mythic, frequency: '~0.2%' },
  };
  return { tier, ...info[tier] };
}

/**
 * Get display rarity for a card (tier + direction or special event name)
 * - SPECIAL: Manually designated days from SPECIAL_DAYS
 * - BLACK SWAN: extreme crash (≤ -30%)
 * - SUPERNOVA: extreme pump (≥ +30%)
 * - Otherwise: TIER + DIRECTION (e.g., "EPIC BEARISH")
 */
export function getDisplayRarity(changePercent: number, _dayId?: number, date?: string): {
  display: string;
  tier: RarityTier;
  isSpecialEvent: boolean;
  eventType: 'black_swan' | 'supernova' | 'special' | null;
  color: string;
} {
  // Check for manually designated special days (including Genesis)
  if (date && SPECIAL_DAYS[date]) {
    const specialDay = getSpecialDayConfig(date);
    return {
      display: specialDay?.name.toUpperCase() || 'SPECIAL EVENT',
      tier: 'mythic',
      isSpecialEvent: true,
      eventType: 'special',
      color: specialDay?.color || MYTHIC_EVENT_COLORS.special,
    };
  }

  // Check for extreme events (mythic events) - these use fixed % thresholds
  if (changePercent <= MYTHIC_EVENT_THRESHOLDS.black_swan) {
    return {
      display: 'BLACK SWAN',
      tier: 'mythic',
      isSpecialEvent: true,
      eventType: 'black_swan',
      color: MYTHIC_EVENT_COLORS.black_swan,
    };
  }

  if (changePercent >= MYTHIC_EVENT_THRESHOLDS.supernova) {
    return {
      display: 'SUPERNOVA',
      tier: 'mythic',
      isSpecialEvent: true,
      eventType: 'supernova',
      color: MYTHIC_EVENT_COLORS.supernova,
    };
  }

  // Regular tier + direction - use percentage-based classification
  const tier = classifyRarity(changePercent);
  const isBullish = changePercent >= 0;
  const direction = isBullish ? 'BULLISH' : 'BEARISH';

  return {
    display: `${TIER_LABELS[tier].toUpperCase()} ${direction}`,
    tier,
    isSpecialEvent: false,
    eventType: null,
    color: TIER_COLORS[tier],
  };
}

// ============================================
// Hidden Traits (Flavor)
// ============================================

/**
 * Calculate momentum trait from price change percentage
 * Uses absolute value of change to classify movement magnitude
 * Thresholds from ETH historical analysis
 */
export function getMomentumTrait(changePercent: number): MomentumTrait {
  const absChange = Math.abs(changePercent);

  if (absChange >= 20) return 'PARABOLIC';
  if (absChange >= 12) return 'EXPLOSION';
  if (absChange >= 7) return 'SURGE';
  if (absChange >= 4) return 'MOVE';
  if (absChange >= 2) return 'DRIFT';
  return 'FLATLINE';
}

/**
 * Calculate volatility trait from OHLC data
 * Formula: (high - low) / open × 100
 * Thresholds from ETH historical analysis (3,747 days)
 */
export function getVolatilityTrait(ohlc: { open: number; high: number; low: number }): VolatilityTrait {
  const range = ((ohlc.high - ohlc.low) / ohlc.open) * 100;

  if (range >= 28) return 'CHAOS';
  if (range >= 17) return 'WILD';
  if (range >= 11) return 'VOLATILE';
  if (range >= 7) return 'ACTIVE';
  if (range >= 4) return 'STEADY';
  return 'CALM';
}

/**
 * Calculate volume trait from trading volume
 * Compares current volume to 30-day average
 * @param volume - Current day's volume
 * @param avgVolume - 30-day average volume (if not provided, returns NORMAL)
 */
export function getVolumeTrait(volume: number, avgVolume?: number): VolumeTrait {
  if (!avgVolume || avgVolume === 0) return 'NORMAL';

  const ratio = volume / avgVolume;

  if (ratio >= 5) return 'TSUNAMI';
  if (ratio >= 3.2) return 'MASSIVE';
  if (ratio >= 2) return 'HEAVY';
  if (ratio >= 1.3) return 'ELEVATED';
  if (ratio >= 0.7) return 'NORMAL';
  if (ratio >= 0.4) return 'QUIET';
  return 'SILENT';
}

/**
 * Calculate all hidden traits for a day
 * @param data - Day's market data
 * @param avgVolume - Optional 30-day average volume
 */
export function calculateTraits(data: DayData, avgVolume?: number): CardTraits {
  return {
    momentum: getMomentumTrait(data.change.percent),
    volatility: getVolatilityTrait(data.ohlc),
    volume: getVolumeTrait(data.market.volume, avgVolume),
  };
}
